import React, { useContext, useEffect, useRef, useState } from "react";
import "./style.css";

import Title from "antd/es/typography/Title";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  notification,
  Row,
  Steps,
} from "antd";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { IoKeyOutline } from "react-icons/io5";
import { Helmet } from "react-helmet";
import Logo from "../../../theme/images/logo_oseo_valais_retina.png";
import { UserContext } from "../../../engine/context/User.context";
import back from "../../../theme/images/back.jpeg";
import { renderUserName } from "../../../utils/constants";
import { getUserByToken } from "../../../engine/api/apiRequest";
import { getLogoByCustomer } from "../../../engine/customerConfig";
import { t } from "i18next";
import LanguageSelector from "../../../shared/components/LanguageSelector";

const { Step } = Steps;

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
};

export default function PublicRegister() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [current, setCurrent] = useState(0);
  const [currentError, setCurrentError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [form] = Form.useForm();

  const navigate = useNavigate();

  const token = searchParams.get("token");

  useEffect(() => {
    const initUserToken = async () => {
      try {
        let user = await getUserByToken(token);

        if (user === "") {
          navigate("/", { replace: true });
          notification.error({
            message: `Ce lien n'est plus valable`,
          });
        } else {
          setUserData(user);
          form.setFieldsValue({
            email: user.email,
            lastname: user.info.lastname,
            firstname: user.info.firstname,
          });
        }
      } catch (e) {
        if (e.response.status === 400) {
          notification.error({
            message: "Ce lien d'invitation a expiré.",
            duration: 5000,
          });

          try {
            navigate("/", { replace: true });
          } catch (e) {
            console.log(e);
          }
        }
        console.log(e);
      }
    };

    if (token === null) {
      navigate("/", { replace: true });
    } else {
      initUserToken();
    }
  }, []);

  const next = () => {
    setCurrent(current + 1);
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const app = useContext(UserContext);

  const [errorRegister, setErrorRegister] = useState("");
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [engagement, setEngagement] = useState(false);

  useEffect(() => {
    if (errorRegister.length > 0 && errorRegister === "email") {
      notification.error({
        message: "Cet email est déja dans notre systéme !",
      });
    }
  }, [errorRegister]);

  const validatePassword = (_, value) => {
    if (!value) {
      return Promise.reject(new Error(t("publicRegister.passwordRequired")));
    }
    if (value.length < 8) {
      return Promise.reject(new Error(t("publicRegister.passwordMinLength")));
    }
    if (!/[a-z]/.test(value)) {
      return Promise.reject(new Error(t("publicRegister.passwordLowercase")));
    }
    if (!/[A-Z]/.test(value)) {
      return Promise.reject(new Error(t("publicRegister.passwordUppercase")));
    }
    if (!/\d/.test(value)) {
      return Promise.reject(new Error(t("publicRegister.passwordNumber")));
    }
    if (!/[!@#$%^&*(),.?":{}|<>_\-\/\\[\]`~]/.test(value)) {
      return Promise.reject(
        new Error(t("publicRegister.passwordSpecialCharacter")),
      );
    }
    return Promise.resolve();
  };

  const onFinish = async values => {
    setLoading(true);

    const data = form.getFieldsValue(true);

    const mergedData = {
      password: data.password,
    };

    const register = await app.registerNewUser(mergedData, token);
    if (register === true) {
      setRegisterSuccess(true);
    } else {
      setErrorRegister(register);
    }
    setLoading(false);
  };

  const validateStep = async step => {
    let error = false;

    try {
      switch (step) {
        case 0:
          await form.validateFields(["email", "password", "confirm", "accept"]);
          break;
        case 1:
          await form.validateFields([
            "lastname",
            "firstname",
            "phone",
            "birthdate",
          ]);
          break;
        case 2:
          break;
        default:
          break;
      }
      setCurrentError(null);
    } catch (e) {
      setCurrentError(step);
      error = true;
    }

    if (!error && step <= 1) {
      next();
    }
  };

  const steps = [
    {
      title: t("publicRegister.credentials"),
      icon: <IoKeyOutline />,
      content: (
        <Row className="register-forms" gutter={[10, 10]}>
          {userData && (
            <>
              <Title level={3}>
                {t("publicRegister.welcomeTitle")} {renderUserName(userData)}
              </Title>
            </>
          )}

          <Title level={4}>{t("publicRegister.passwordPrompt")}</Title>
          <Col span={24}>
            <Form.Item
              disabled={true}
              normalize={value => (value || "").toLowerCase()}
              label={t("publicRegister.emailLabel")}
              name="email"
              rules={[
                {
                  type: "email",
                  message: t("publicRegister.emailInvalid"),
                },
                {
                  required: true,
                  message: t("publicRegister.emailRequired"),
                },
              ]}
            >
              <Input disabled={true} />
            </Form.Item>
            <Form.Item
              name="password"
              label={"Mot de passe"}
              rules={[
                {
                  required: true,
                  message: t("publicRegister.passwordRequired"),
                },
                { validator: validatePassword },
              ]}
              hasFeedback
            >
              <Input.Password />
            </Form.Item>
            <Form.Item
              name="confirm"
              label={t("publicRegister.confirmPasswordLabel")}
              dependencies={["password"]}
              hasFeedback
              rules={[
                {
                  required: true,
                  message: t("publicRegister.confirmPasswordRequired"),
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(t("publicRegister.passwordsDoNotMatch")),
                    );
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>
            <br />
            <a
              href={
                "https://www.oseo-vs.ch/wp-content/uploads/2023/09/engagement_confidentialite_plateforme_avic.pdf"
              }
            >
              {t("publicRegister.confidentialityEngagement")}
            </a>
            <br />
            <br />
            <label>
              <Checkbox
                checked={engagement}
                onChange={() => {
                  setEngagement(!engagement);
                }}
              />
              <span style={{ marginLeft: 20 }}>
                {t("publicRegister.confirmEngagement")}
              </span>
            </label>
          </Col>
        </Row>
      ),
    },
  ];

  const renderStatusStep = index => {
    if (currentError === index) {
      return "error";
    }

    if (index === current) {
      return "process";
    }

    if (current > index) {
      return "finish";
    }

    return "wait";
  };

  const ref = useRef();

  return (
    <div id={"registerPage"} style={{ backgroundImage: "url(" + back + ")" }}>
      <Card className={"center-vw-full registerBG"}>
        <LanguageSelector lang={"fr"} />
        {registerSuccess ? (
          <div style={{ textAlign: "center", padding: 20 }}>
            <img
              src={getLogoByCustomer()}
              style={{
                width: "200px",
                margin: "0 auto",
                display: "block",
                marginTop: "0px",
              }}
            />
            <br />
            <Title level={2}>
              {t("publicRegister.registrationSuccessTitle")}
            </Title>
            <br />
            <p style={{ fontSize: "20px" }}>
              {t("publicRegister.registrationSuccessMessage")}
            </p>
            <Link to={"/auth"}>
              <Button style={{ maxWidth: "300px" }} type={"primary"}>
                {t("publicRegister.loginNow")}
              </Button>
            </Link>
          </div>
        ) : (
          <Form
            ref={ref}
            tabIndex={0}
            {...formItemLayout}
            form={form}
            layout={"vertical"}
            name="register"
            onFinish={onFinish}
          >
            <Steps
              current={current}
              type="navigation"
              responsive
              className="site-navigation-steps"
            >
              {steps.map((item, k) => (
                <Step
                  key={item.title}
                  title={item.title}
                  icon={item.icon}
                  status={renderStatusStep(k)}
                />
              ))}
            </Steps>

            <div className="steps-content">{steps[current].content}</div>
            <div className="steps-action">
              <Button
                disabled={!engagement}
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{ width: "250px" }}
              >
                {t("publicRegister.confirmAccount")}
              </Button>
            </div>
            <p style={{ color: "#ff4d4f" }}>
              {errorRegister === "email" && t("publicRegister.emailExists")}
            </p>
            <Col span={12}>
              <Link to={"/"}>
                <p> {t("publicRegister.alreadyHaveAccount")}</p>
              </Link>
            </Col>
          </Form>
        )}
      </Card>
    </div>
  );
}
